import { FormEvent, useState } from 'react';
import { api, EventDetails, OpenInvite, RequestError } from '../lib/api';
import { useAnimatedClose } from '../hooks/useAnimatedClose';

interface Props { invite: OpenInvite; event: EventDetails; token: string; onDone: (status: 'yes' | 'no', count: number) => void; onCancel: () => void; onShowDetails: () => void; }

export function RsvpFlow({ invite, event, token, onDone, onCancel, onShowDetails }: Props) {
  const { closing, requestClose } = useAnimatedClose(onCancel);
  const [status, setStatus] = useState<'yes' | 'no' | null>(null);
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [name, setName] = useState(invite.name);
  const [email, setEmail] = useState(invite.email || '');
  const [phone, setPhone] = useState(invite.phone || '');
  const [note, setNote] = useState('');
  const [showJeffism, setShowJeffism] = useState(false);
  const [jeffism, setJeffism] = useState('');
  const [credit, setCredit] = useState(invite.name);
  const [anonymous, setAnonymous] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const attendeeCount = adults + kids;
  const changeAdults = (delta: number) => setAdults((current) => Math.min(invite.maxPartySize - kids, Math.max(0, current + delta)));
  const changeKids = (delta: number) => setKids((current) => Math.min(invite.maxPartySize - adults, Math.max(0, current + delta)));
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError('');
    if (!status) { setError('Choose yes or no.'); return; }
    if (!name.trim()) { setError('Add your name.'); return; }
    if (!email.trim() && !phone.trim()) { setError('Add an email address or phone number.'); return; }
    if (status === 'yes' && attendeeCount < 1) { setError('Add at least one adult or child.'); return; }
    setSubmitting(true);
    try {
      await api(`/api/invite/${token}/rsvp`, { method: 'POST', body: JSON.stringify({
        status, attendeeCount: status === 'yes' ? attendeeCount : 0, adultCount: status === 'yes' ? adults : 0,
        childCount: status === 'yes' ? kids : 0, name, email, phone, note,
        jeffism: showJeffism ? jeffism : '', jeffismCredit: showJeffism ? credit : '', jeffismAnonymous: anonymous,
      }) });
      requestClose(() => onDone(status, status === 'yes' ? attendeeCount : 0));
    } catch (caught) {
      setError(caught instanceof RequestError && caught.code === 'ALREADY_SUBMITTED' ? "That RSVP was already submitted. You're on the list." : caught instanceof Error ? caught.message : 'Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className={`rsvp-backdrop ${closing ? 'is-closing' : ''}`} onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}>
      <section className="rsvp-card" aria-labelledby="rsvp-title">
        <button className="icon-button" onClick={() => requestClose()} aria-label="Close RSVP form">×</button>
        <p className="eyebrow">Your invitation</p>
        <h2 id="rsvp-title">Can you make it?</h2>
        <div className="rsvp-event-summary" aria-label="Event summary">
          <span><i className="fa-regular fa-calendar-days" aria-hidden="true" /> {event.date}</span>
          <span><i className="fa-regular fa-clock" aria-hidden="true" /> Guests arrive {event.guestArrivalTime}</span>
          <span><i className="fa-solid fa-location-dot" aria-hidden="true" /> {event.venueName}</span>
          <button type="button" onClick={() => requestClose(onShowDetails)}>Full event details <i className="fa-solid fa-arrow-right" aria-hidden="true" /></button>
        </div>
        <p className="gift-notice"><i className="fa-solid fa-gift" aria-hidden="true" /><strong>No gifts, please. Your presence is our present!</strong></p>
        <form onSubmit={submit} noValidate>
          <fieldset className="choice-fieldset">
            <legend className="sr-only">Can you attend?</legend>
            <button type="button" className={`big-choice ${status === 'yes' ? 'selected' : ''}`} onClick={() => setStatus('yes')} aria-pressed={status === 'yes'}>YES</button>
            <button type="button" className={`big-choice ${status === 'no' ? 'selected no' : ''}`} onClick={() => setStatus('no')} aria-pressed={status === 'no'}>NO</button>
          </fieldset>
          {status && <div className="form-reveal">
            {status === 'yes' && <>
              <p className="question-label">Who's coming?</p>
              <div className="attendance-steppers">
                <div className="attendance-stepper">
                  <span>Adults</span>
                  <div className="stepper" aria-label="Adults">
                    <button type="button" onClick={() => changeAdults(-1)} aria-label="Decrease adults" disabled={adults === 0}>−</button>
                    <output aria-live="polite" aria-label={`${adults} ${adults === 1 ? 'adult' : 'adults'}`}>{adults}</output>
                    <button type="button" onClick={() => changeAdults(1)} aria-label="Increase adults" disabled={attendeeCount >= invite.maxPartySize}>+</button>
                  </div>
                </div>
                <div className="attendance-stepper">
                  <span>Children</span>
                  <div className="stepper" aria-label="Children">
                    <button type="button" onClick={() => changeKids(-1)} aria-label="Decrease children" disabled={kids === 0}>−</button>
                    <output aria-live="polite" aria-label={`${kids} ${kids === 1 ? 'child' : 'children'}`}>{kids}</output>
                    <button type="button" onClick={() => changeKids(1)} aria-label="Increase children" disabled={attendeeCount >= invite.maxPartySize}>+</button>
                  </div>
                </div>
              </div>
              <p className="help"><strong>{attendeeCount}</strong> attending · This invitation allows up to {invite.maxPartySize}.</p>
            </>}
            {status === 'no' && <p className="soft-message">We'll miss you. A quick confirmation is all we need.</p>}
            <div className="contact-fields">
              <label>Name<input value={name} maxLength={120} autoComplete="name" onChange={(e) => setName(e.target.value)} required /></label>
              <label>Email<input value={email} maxLength={254} type="email" autoComplete="email" onChange={(e) => setEmail(e.target.value)} /></label>
              <label>Phone<input value={phone} maxLength={40} type="tel" autoComplete="tel" onChange={(e) => setPhone(e.target.value)} /></label>
            </div>
            {status === 'no' && <label>Optional note<textarea value={note} maxLength={500} rows={2} onChange={(e) => setNote(e.target.value)} /></label>}
            <button className="jeffism-toggle" type="button" onClick={() => setShowJeffism(!showJeffism)} aria-expanded={showJeffism}><i className="fa-solid fa-quote-left" aria-hidden="true" /> Add a Jeffism</button>
            {showJeffism && <div className="jeffism-box">
              <h3>Jeffisms: 70 Years of Unfiltered Commentary</h3>
              <p>Got a classic Jeff quote, piece of questionable wisdom, or a very short story? Add it to the archives.</p>
              <label>Quote or story<textarea value={jeffism} maxLength={280} rows={4} onChange={(e) => setJeffism(e.target.value)} /></label>
              <div className="character-count" aria-live="polite">{jeffism.length} / 280</div>
              <label>Credit this to<input value={credit} maxLength={120} onChange={(e) => setCredit(e.target.value)} /></label>
              <label className="checkbox"><input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} /> Keep me anonymous in the slideshow</label>
            </div>}
            {error && <p className="form-error" role="alert">{error}</p>}
            <button className="button primary full submit-rsvp" type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Lock it in'}</button>
            <p className="one-time-note">Your RSVP can be submitted once. Need a correction later? Contact the organizer.</p>
          </div>}
        </form>
      </section>
    </div>
  );
}
